import React, { useState, useEffect } from "react";
import { Accordion, Modal } from "react-bootstrap";
import DropFileInput from "../../../pages/reusable_components/drop_file_component/DropFileComponent"; // Import the DropFileInput component
import styles from "./ClientFilesUploader.module.css";
import {
  deleteDocumentForClient,
  fetchDocumentForClient,
  fetchDocumentTypes,
  uploadDocumentForClient,
} from "./ClientFilesUploader.service";
import { ThreeCircles } from "react-loader-spinner";
import * as Icon from "react-bootstrap-icons";
import {
  ClientDocument,
  ClientFilesUploaderProps,
} from "./ClientFilesUploader.utils";
import UploadedFileViewer from "./components/UploadedFileViewer/UploadedFileViewer";
import { FileInputTypes } from "../ClientsGrid/ClientsGrid.utils";

/**
 * ClientFilesUploader
 *
 * Modal-based component to upload, preview, and delete client documents.
 * - Fetches existing files by document type when modal opens.
 * - Uses DropFileInput to handle drag-and-drop or manual file uploads.
 * - Tracks upload progress per document type.
 * - Provides inline viewer for PDFs.
 * - Allows deleting previously uploaded documents.
 */
const ClientFilesUploader: React.FC<ClientFilesUploaderProps> = ({
  show,
  clientMasterId,
  clientName,
  isPublic,
  onHide,
  refresh,
}) => {
  const [uploadProgressByDocType, setUploadProgressByDocType] = useState<{
    [firmReferenceCode: string]: boolean;
  }>({});
  const [existingDocuments, setExistingDocuments] = useState<{
    [firmReferenceCode: string]: ClientDocument[]; // or more specific type if available
  }>({});
  const [previewPdfs, setPreviewPdfs] = useState<
    { url: string; name: string }[]
  >([]);
  const [fileTypes, setFileTypes] = useState<FileInputTypes[]>([])
  const [apiProgress, setApiProgress] = useState<boolean>(false)

  /**
   * Fetch a specific document type (optionally by clientDocumentId)
   */
  const fetchExistingDocument = async (
    clientDocumentId: number,
    documentTypeCode: string
  ) => {
    const data = await fetchDocumentForClient(
      clientMasterId,
      (progress) => {},
      documentTypeCode,
    );
    if (data) {
      setExistingDocuments((prev) => ({
        ...prev,
        [documentTypeCode]: data,
      }));
    }
  };

  /**
   * Handles file uploads per document type.
   */
  const handleFileChange = async (
    updatedList: File[],
    event: React.ChangeEvent<HTMLInputElement>,
    documentTypeId: number,
    firmReferenceCode: string
  ) => {
    if (!updatedList.length) return;

    const files = updatedList;

    // Set upload in progress
    setUploadProgressByDocType((prev) => ({
      ...prev,
      [firmReferenceCode]: true,
    }));

    // Create metadata per file
    const documentMetaData = files.map((file) => ({
      DocumentTypeCode: firmReferenceCode,
      FileName: file.name,
      Status: "Completed",
    }));

    const response = await uploadDocumentForClient(
      clientMasterId,
      documentMetaData,
      files,
      (progress) =>
        setUploadProgressByDocType((prev) => ({
          ...prev,
          [firmReferenceCode]: progress,
        }))
    );

    // Handle multi-document response
    if (response.success && response.data[0].Documents?.length > 0) {
      const uploadedMetadata: ClientDocument[] = response.data[0].Documents.map(
        (doc: any) => ({
          ClientDocumentId: doc.ClientDocumentId,
          FirmId: response.data[0].FirmId,
          ClientMasterId: response.data[0].ClientMasterId,
          ClientName: response.data[0].ClientName || "",
          FileName: doc.FileName,
          BlobUrl: doc.BlobUrl,
          DocumentTypeCode: doc.DocumentTypeCode,
          DocumentTypeName: "", // optional
          Status: "Completed",
          Active: doc.Active,
          CreatedBy: "", // optional
          CreatedDate: new Date().toISOString(), // fallback
          UpdatedBy: "", // optional
          UpdatedDate: new Date().toISOString(), // fallback
        })
      );
      setExistingDocuments((prev) => ({
        ...prev,
        [firmReferenceCode]: [...prev[firmReferenceCode], ...uploadedMetadata],
      }));
    }
  };

  /**
   * Handles file deletion by calling the delete API and updating local state.
   */
  const handleDeleteDocument = async (
    file: ClientDocument,
    referenceCode: string,
    clientDocumentId: number
  ) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete ${file.FileName}?`
    );
    if (!confirmed) return;
    // Call delete API here
    const success = await deleteDocumentForClient(clientDocumentId); // Replace with your logic
    if (success) {
      setExistingDocuments((prev) => ({
        ...prev,
        [referenceCode]: prev[referenceCode].filter(
          (f) => f.ClientDocumentId !== clientDocumentId
        ),
      }));
    }
  };

  useEffect(() => {
    fetchDocumentTypes(setFileTypes, setApiProgress, isPublic)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Load all document types for this client on modal open.
   */
  useEffect(() => {
    if (show && fileTypes.length > 0) {
      fileTypes.forEach((input) => {
        fetchExistingDocument(
          input.FirmReferenceDataId,
          input.ReferenceDataCode
        );
      });
    } else{
      setPreviewPdfs([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, fileTypes]);

  return (
    <>
      {previewPdfs.map((file, index) => (
        <UploadedFileViewer
          key={`${file.url}-${index}`}
          preview={file}
          onClose={() =>
            setPreviewPdfs((prev) =>
              prev.filter((f) => f.url !== file.url || f.name !== file.name)
            )
          }
        />
      ))}

      <Modal
        show={show}
        onHide={() => onHide()}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static" // Prevents closing the modal by clicking outside
        centered
        scrollable={false}
        className={styles.uploadFilesModal}
        keyboard={false}
      >
        <Modal.Header className={styles.uploadFilesModalHeader} closeButton>
          <div className={styles.clientNameWrapper}>
            <b>{clientName}</b>
          </div>
        </Modal.Header>
        <Modal.Body className={styles.uploadFilesModalBody}>
          {!apiProgress ? (
            <>
              <span>Please upload required client documents</span>
              <Accordion className={styles.uploadFilesAccordion}>
                {fileTypes.map((input, index) => (
                  <Accordion.Item
                    eventKey={String(index)}
                    key={input.FirmReferenceDataId}
                  >
                    <Accordion.Header
                      className={styles.uploadFilesAccordianHeader}
                    >
                      <div className={styles.uploadFilesAccordianHeaderContent}>
                        <div>{input.ReferenceDataValue}</div>
                        {uploadProgressByDocType[input.ReferenceDataCode] ||
                        !existingDocuments[input.ReferenceDataCode] ? (
                          <div style={{ marginRight: 10 }}>
                            <ThreeCircles
                              height="20"
                              width="20"
                              color="#4fa94d"
                              wrapperStyle={{}}
                              wrapperClass=""
                              visible={true}
                              ariaLabel="three-circles-rotating"
                              outerCircleColor="var(--theme1_tertiary)"
                              innerCircleColor="var(--theme1_secondary)"
                              middleCircleColor="var(--theme1_primary)"
                            />
                          </div>
                        ) : (
                          <div
                            className={
                              styles.existingFileInfo +
                              " " +
                              (existingDocuments[input.ReferenceDataCode]
                                ?.length > 0
                                ? styles.availableFiles
                                : "")
                            }
                          >
                            {existingDocuments[input.ReferenceDataCode]
                              ?.length || 0}
                          </div>
                        )}
                      </div>
                    </Accordion.Header>
                    <Accordion.Body className={styles.uploadFilesAccordianBody}>
                      <div className={styles.dropFileWrapper}>
                        <DropFileInput
                          onFileChange={(
                            files: File[],
                            event: React.ChangeEvent<HTMLInputElement>
                          ) =>
                            handleFileChange(
                              files,
                              event,
                              input.FirmReferenceDataId,
                              input.ReferenceDataCode
                            )
                          }
                          uploading={
                            uploadProgressByDocType[input.ReferenceDataCode]
                          }
                          message={`Upload your ${input.ReferenceDataValue} document`}
                        />
                      </div>
                      {existingDocuments[input.ReferenceDataCode]?.length >
                        0 && (
                        <div className={styles.uploadedFileList}>
                          <div className={styles.uploadedFileTitle}>
                            Uploaded Files:
                          </div>

                          {existingDocuments[input.ReferenceDataCode].map(
                            (file, idx) => (
                              <div key={idx} className={styles.uploadedFileRow}>
                                <div className={styles.uploadedFileName}>
                                  <Icon.FilePdfFill size={16} />
                                  <span>{file.FileName}</span>
                                </div>

                                <div className={styles.uploadedFileActions}>
                                  <Icon.Eye
                                    className={styles.viewIcon}
                                    title="View Document"
                                    onClick={() =>
                                      setPreviewPdfs((prev) => [
                                        ...prev,
                                        {
                                          url: file.BlobUrl,
                                          name: file.FileName,
                                        },
                                      ])
                                    }
                                  />
                                  <Icon.Trash
                                    className={styles.deleteIcon}
                                    title="Delete Document"
                                    onClick={() =>
                                      handleDeleteDocument(
                                        file,
                                        input.ReferenceDataCode,
                                        file.ClientDocumentId
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </>
          ) : (
            <div className="flex-row-center-center">
              <div
                className="spinner flex-column-center-center"
                style={{ fontSize: 14, fontWeight: 500 }}
              >
                <ThreeCircles
                  height="50"
                  width="50"
                  color="#4fa94d"
                  wrapperStyle={{}}
                  wrapperClass=""
                  visible={true}
                  ariaLabel="three-circles-rotating"
                  outerCircleColor="var(--theme1_tertiary)"
                  innerCircleColor="var(--theme1_secondary)"
                  middleCircleColor="var(--theme1_primary)"
                />
                Fetching details of the documents to be uploaded
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer></Modal.Footer>
      </Modal>
    </>
  );
};

export default ClientFilesUploader;
